/**
 * order-visuals — shared Trioty status/action color model used by the orders
 * table and the orders card grid, so both stay pixel-identical to the design.
 *
 * `fulVisual` drives the table's progress ring; `fulPill` drives the card's
 * fulfillment pill; `actVisual` + `actColors` drive the next-action button in
 * both. Colors are theme-aware (light / dark) via `pickTheme`.
 */

import type { OrderStatus } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { useThemeStore } from "@/shared/theme/useTheme";
import { statusLabelKey } from "@/modules/orders/lib/order-status-display";

export function pickTheme<T>(isDark: boolean, light: T, dark: T): T {
  return isDark ? dark : light;
}

/** Fulfillment-stage color + progress-ring shape (Trioty statusRing/fulMeta).
 * `frac` is how much of the ring's pie fills: unfulfilled 0.25 → preparing 0.5
 * → shipped 0.75 → delivered (full + check). Cancelled draws an X. */
export function fulVisual(
  status: OrderStatus,
  isDark: boolean,
): { color: string; ring: "check" | "x" | "wedge"; frac: number } {
  switch (status) {
    case "delivered":
      return { color: "var(--text-primary)", ring: "check", frac: 1 };
    // Shipped → next action "mark delivered" → green
    case "shipped":
      return { color: "var(--success-500)", ring: "wedge", frac: 0.75 };
    // Preparing → next action "mark shipped" → blue
    case "preparing":
      return { color: pickTheme(isDark, "#2F6FE0", "#7CA8FF"), ring: "wedge", frac: 0.5 };
    // Closed states → dimmed gray, not alarming red
    case "cancelled":
    case "declined":
      return { color: "var(--text-disabled)", ring: "x", frac: 0 };
    // Awaiting payment review → urgent red
    case "pending_payment_verification":
    case "paid_less_than_required":
      return { color: pickTheme(isDark, "#E0353B", "#F87171"), ring: "wedge", frac: 0.25 };
    // Everything else in-flight (confirm / buyer-wait / draft) → amber
    default:
      return { color: pickTheme(isDark, "#D9910F", "#FBBF6B"), ring: "wedge", frac: 0.25 };
  }
}

/** Fulfillment pill colors (Trioty fulMeta): bg tint + text + dot. */
export function fulPill(
  status: OrderStatus,
  isDark: boolean,
): { bg: string; text: string; dot: string } {
  switch (status) {
    case "delivered":
      return { bg: "var(--soft2)", text: "var(--text-primary)", dot: "var(--text-muted)" };
    // Shipped → mark delivered → green
    case "shipped":
      return {
        bg: "var(--success-100)",
        text: "var(--success-500)",
        dot: "var(--success-500)",
      };
    // Preparing → mark shipped → blue
    case "preparing":
      return {
        bg: pickTheme(isDark, "#E9F0FE", "rgba(59,130,246,.16)"),
        text: pickTheme(isDark, "#2563EB", "#7CA8FF"),
        dot: "#3B82F6",
      };
    // Closed states → dimmed gray
    case "cancelled":
    case "declined":
      return { bg: "var(--soft2)", text: "var(--text-muted)", dot: "var(--text-muted)" };
    // Awaiting payment review → urgent red
    case "pending_payment_verification":
    case "paid_less_than_required":
      return {
        bg: pickTheme(isDark, "#FCEDED", "rgba(239,68,68,.16)"),
        text: pickTheme(isDark, "#DC2626", "#F87171"),
        dot: pickTheme(isDark, "#E5484D", "#EF4444"),
      };
    default:
      return {
        bg: pickTheme(isDark, "#FBF1E3", "rgba(245,158,11,.16)"),
        text: pickTheme(isDark, "#B45309", "#FBBF6B"),
        dot: pickTheme(isDark, "#D97706", "#F59E0B"),
      };
  }
}

export type ActTone = "red" | "gray" | "blue" | "green" | "amber";

/** Next-action tone + glyph (Trioty nextAction). null → view-only. */
export function actVisual(
  status: OrderStatus,
): { tone: ActTone; icon: "card" | "clipboard" | "truck" | "home" } | null {
  switch (status) {
    case "pending_payment_verification":
    case "paid_less_than_required":
      return { tone: "red", icon: "card" };
    case "pending_seller_confirmation":
    case "confirmed":
      return { tone: "amber", icon: "clipboard" };
    case "preparing":
      return { tone: "blue", icon: "truck" };
    case "shipped":
      return { tone: "green", icon: "home" };
    default:
      return null;
  }
}

/** Row accent + action-button fill for a next-action tone (Trioty actTone). */
export function actColors(
  tone: ActTone,
  isDark: boolean,
): { accent: string; row: string; btnBg: string; btnFg: string } {
  switch (tone) {
    case "red":
      return {
        accent: pickTheme(isDark, "#E0353B", "#F87171"),
        row: "rgba(224,53,59,.08)",
        btnBg: pickTheme(isDark, "#E0353B", "#F87171"),
        btnFg: "#fff",
      };
    case "blue":
      return {
        accent: pickTheme(isDark, "#2F6FE0", "#8FB4FF"),
        row: pickTheme(isDark, "rgba(47,111,224,.06)", "rgba(59,130,246,.10)"),
        btnBg: pickTheme(isDark, "#E7EFFC", "rgba(59,130,246,.22)"),
        btnFg: pickTheme(isDark, "#2F6FE0", "#8FB4FF"),
      };
    case "green":
      return {
        accent: pickTheme(isDark, "#16A34A", "#4ADE80"),
        row: pickTheme(isDark, "rgba(22,163,74,.06)", "rgba(74,222,128,.10)"),
        btnBg: pickTheme(isDark, "#E4F5EA", "rgba(74,222,128,.20)"),
        btnFg: pickTheme(isDark, "#16A34A", "#4ADE80"),
      };
    case "amber":
      return {
        accent: pickTheme(isDark, "#D9910F", "#FBBF6B"),
        row: pickTheme(isDark, "rgba(217,145,15,.08)", "rgba(245,158,11,.10)"),
        btnBg: pickTheme(isDark, "#FBF0DA", "rgba(245,158,11,.20)"),
        btnFg: pickTheme(isDark, "#D9910F", "#FBBF6B"),
      };
    case "gray":
    default:
      return {
        accent: "var(--text-primary)",
        row: pickTheme(isDark, "rgba(100,116,139,.06)", "rgba(148,163,184,.08)"),
        btnBg: "var(--accent-500)",
        btnFg: "var(--on-accent)",
      };
  }
}

/** Progress ring (Trioty statusRing): an outline ring with a pie wedge filling
 * `frac` of it, a full fill + check for delivered, or an X for cancelled. */
export function StatusRing({
  color,
  kind,
  frac,
  isDark,
  size = 15,
}: {
  color: string;
  kind: "check" | "x" | "wedge";
  frac: number;
  isDark: boolean;
  size?: number;
}) {
  const r = 5;
  const th = frac * 2 * Math.PI;
  const ex = (12 + r * Math.sin(th)).toFixed(2);
  const ey = (12 - r * Math.cos(th)).toFixed(2);
  const large = frac > 0.5 ? 1 : 0;
  const wedge = `M12 12 L12 ${12 - r} A ${r} ${r} 0 ${large} 1 ${ex} ${ey} Z`;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
      <circle cx="12" cy="12" r="9" fill="none" stroke={color} strokeWidth="2" />
      {kind === "check" ? (
        <>
          <circle cx="12" cy="12" r="9" fill={color} />
          <path d="M8.2 12.2l2.5 2.5 5-5.4" fill="none" stroke={isDark ? "#0d0d0f" : "#fff"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </>
      ) : kind === "x" ? (
        <path d="M9 9l6 6M15 9l-6 6" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
      ) : (
        <path d={wedge} fill={color} />
      )}
    </svg>
  );
}

/** The next-action glyph inside the action button. */
export function ActionGlyph({ kind }: { kind: "card" | "clipboard" | "truck" | "home" }) {
  switch (kind) {
    case "card":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <path d="M2 10h20" />
        </svg>
      );
    case "clipboard":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="8" y="2" width="8" height="4" rx="1" />
          <path d="M9 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3" />
          <path d="m9 14 2 2 4-4" />
        </svg>
      );
    case "truck":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M10 17h4V5H2v12h3" />
          <path d="M14 8h4l3 3v6h-3" />
          <circle cx="7.5" cy="17.5" r="1.5" />
          <circle cx="17.5" cy="17.5" r="1.5" />
        </svg>
      );
    case "home":
    default:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5 9.5V21h14V9.5" />
          <path d="M9 21v-6h6v6" />
        </svg>
      );
  }
}

/**
 * OrderStatusBadge — the single, reusable order-status indicator: the Trioty
 * progress ring + stage-colored label. Used in the table, the card grid, and
 * the detail drawer so the status reads identically everywhere.
 */
export function OrderStatusBadge({
  status,
  className,
}: {
  status: OrderStatus;
  className?: string;
}) {
  const t = useTranslations();
  const isDark = useThemeStore((s) => s.resolvedDark);
  const ful = fulVisual(status, isDark);
  return (
    <span className={`inline-flex items-center gap-[6px] ${className ?? ""}`.trim()}>
      <StatusRing color={ful.color} kind={ful.ring} frac={ful.frac} isDark={isDark} />
      <span className="whitespace-nowrap text-[11.5px] font-semibold" style={{ color: ful.color }}>
        {t(statusLabelKey(status))}
      </span>
    </span>
  );
}
